<script setup lang="ts">
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '../stores/auth'

const router = useRouter()
const authStore = useAuthStore()

const agreed = ref(false)
const submitting = ref(false)
const error = ref('')

async function handleSubmit() {
  if (!agreed.value) return

  submitting.value = true
  error.value = ''

  try {
    const success = await authStore.agreeToCoc()
    if (success) {
      router.push('/')
    } else {
      error.value = 'Failed to record agreement. Please try again.'
    }
  } catch (e) {
    error.value = 'An error occurred. Please try again.'
  } finally {
    submitting.value = false
  }
}
</script>

<template>
  <div class="max-w-3xl mx-auto p-4 md:p-6">
    <div class="text-center mb-8">
      <h1 class="text-2xl font-bold text-gray-800 mb-2">Code of Conduct</h1>
      <p class="text-gray-600">Please read and agree to continue</p>
    </div>

    <div class="card mb-6">
      <h2 class="text-lg font-bold text-gray-800 mb-4">Vermont Cabin - Guest Agreement</h2>

      <p class="text-gray-600 mb-6">
        Welcome! To ensure everyone enjoys their stay, please review and agree to the following guidelines:
      </p>

      <div class="space-y-4">
        <div class="flex gap-3">
          <span class="text-xl">1.</span>
          <div>
            <p class="font-medium text-gray-800">Respect the Property</p>
            <p class="text-sm text-gray-600">Treat the cabin as your own home. Report any damages or issues immediately.</p>
          </div>
        </div>

        <div class="flex gap-3">
          <span class="text-xl">2.</span>
          <div>
            <p class="font-medium text-gray-800">Quiet Hours</p>
            <p class="text-sm text-gray-600">10 PM - 8 AM. Be mindful of wildlife and the peaceful mountain environment.</p>
          </div>
        </div>

        <div class="flex gap-3">
          <span class="text-xl">3.</span>
          <div>
            <p class="font-medium text-gray-800">No Smoking</p>
            <p class="text-sm text-gray-600">Smoking is not permitted inside the cabin. Use designated outdoor areas only.</p>
          </div>
        </div>

        <div class="flex gap-3">
          <span class="text-xl">4.</span>
          <div>
            <p class="font-medium text-gray-800">Pets</p>
            <p class="text-sm text-gray-600">Pets must be pre-approved and cleaned up after. No pets on furniture or beds.</p>
          </div>
        </div>

        <div class="flex gap-3">
          <span class="text-xl">5.</span>
          <div>
            <p class="font-medium text-gray-800">Trash & Wildlife</p>
            <p class="text-sm text-gray-600">Take all trash with you when leaving. Never leave food or trash outside - bears are active in the area.</p>
          </div>
        </div>

        <div class="flex gap-3">
          <span class="text-xl">6.</span>
          <div>
            <p class="font-medium text-gray-800">Heating System</p>
            <p class="text-sm text-gray-600">Follow thermostat instructions. The radiant floor heating is automated - please don't adjust manual controls.</p>
          </div>
        </div>

        <div class="flex gap-3">
          <span class="text-xl">7.</span>
          <div>
            <p class="font-medium text-gray-800">Power Conservation</p>
            <p class="text-sm text-gray-600">The cabin runs on solar power. Turn off lights and unplug devices when not in use.</p>
          </div>
        </div>

        <div class="flex gap-3">
          <span class="text-xl">8.</span>
          <div>
            <p class="font-medium text-gray-800">Emergencies</p>
            <p class="text-sm text-gray-600">Contact the owner immediately for any issues or emergencies. Emergency info is in the Guide section.</p>
          </div>
        </div>

        <div class="flex gap-3">
          <span class="text-xl">9.</span>
          <div>
            <p class="font-medium text-gray-800">Check-out</p>
            <p class="text-sm text-gray-600">Leave by check-out time. Strip beds, start dishwasher, take all trash. Leave the cabin as you found it.</p>
          </div>
        </div>

        <div class="flex gap-3">
          <span class="text-xl">10.</span>
          <div>
            <p class="font-medium text-gray-800">Liability</p>
            <p class="text-sm text-gray-600">Guests assume responsibility for their safety and the safety of their party. The cabin is in a remote mountain location with inherent risks.</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Agreement Checkbox -->
    <div class="card mb-6 bg-primary-50 border-primary-200">
      <label class="flex items-start gap-3 cursor-pointer">
        <input
          type="checkbox"
          v-model="agreed"
          class="mt-1 h-5 w-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
        />
        <span class="text-gray-800">
          I have read and agree to follow the Code of Conduct. I understand that violation of these guidelines
          may result in loss of booking privileges.
        </span>
      </label>
    </div>

    <!-- Error Message -->
    <div v-if="error" class="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
      {{ error }}
    </div>

    <!-- Submit Button -->
    <div class="text-center">
      <button
        @click="handleSubmit"
        :disabled="!agreed || submitting"
        class="btn btn-primary px-8 py-3 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <span v-if="submitting">Submitting...</span>
        <span v-else>I Agree - Continue</span>
      </button>
      <p class="text-sm text-gray-500 mt-4">
        Welcome, {{ authStore.user?.name }}! You only need to agree once.
      </p>
    </div>
  </div>
</template>
